import { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from '../Tooltip.jsx';
import Button from '../Button.jsx';
import ChatInput from '../ChatInput/ChatInput.jsx';
import StreamingStatusBar from '../StreamingStatusBar.jsx';
import ChatPanelSkeleton from './ChatPanelSkeleton.jsx';
import ChatPanelAssistantMessageSkeleton from './ChatPanelAssistantMessageSkeleton.jsx';
import ChatMessageMarkdown, { normalizeChatMessageContent } from './ChatMessageMarkdown.jsx';
import RunActivityGroup from './RunActivityGroup.jsx';
import ChatNoticeRow from './ChatNoticeRow.jsx';
import { buildHighlightedHtml, buildMarkdownWithHighlights } from './chatHighlightUtils.js';
import { Copy, FileText } from 'lucide-react';

// Stable empty fallback for activity groups without steps — a fresh [] per
// render would defeat downstream memoization.
const EMPTY_STEPS = [];

const toPlainTextDescription = (value, maxLength = 140) => {
  if (typeof value !== 'string') return '';

  const normalized = value
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/\r?\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return '';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
};

/**
 * @typedef {{ id?: string, messageUuid?: string, role?: string, content?: string, attachments?: Array<{ name?: string, fileType?: string, fileUuid?: string, id?: string }> }} ChatPanelMessage
 */

/**
 * @typedef {object} ChatPanelMessageSearch
 * @property {string} searchValue
 * @property {number} currentMatchIndex
 * @property {Array<{ messageIndex: number, start: number, end: number, globalIndex: number }>} matches
 * @property {(index: number) => (el: HTMLElement|null) => void} setMessageRef
 */

/**
 * Thread + composer for an active conversation (slim legacy ann `ConversationView`).
 * @param {object} props
 * @param {ChatPanelMessage[]} [props.messages]
 * @param {boolean} [props.isLoadingMessages]
 * @param {boolean} [props.agentIsTyping]
 * @param {boolean} [props.isStreaming]
 * @param {string|null} [props.streamingStatusLabel] — Workflow state feedback (e.g. “Generating response…”) shown above the streaming bar while the run is active.
 * @param {ChatPanelMessageSearch|null} [props.messageSearch]
 * @param {string} [props.conversationScrollKey] — when it changes, scroll “pinned to bottom” resets (conversation switch).
 * @param {(message: ChatPanelMessage) => void} [props.onOpenMessageAsDocument]
 * @param {string[]} [props.documentCreatedMessageIds]
 * @param {Record<string, { title?: string, description?: string }>} [props.documentCardMetaByMessageId]
 * @param {boolean} [props.alignMessagesStart] — when true (e.g. fullscreen panel), user + assistant rows align left like a single column.
 * @param {string} [props.activityRunningLabel] — fallback header for a live run-activity group when the latest step has no text.
 * @param {string} [props.activityToggleLabel] — accessible label for the activity group's expand/collapse control.
 * @param {(count: number) => string} [props.formatActivitySummary] — builds the frozen "Ran N steps" header for a completed run-activity group.
 * @param {string} [props.noReplyLabel] — copy for a `role: 'notice'` row of kind `no_reply` (turn completed without text).
 * @param {string} [props.failedLabel] — copy for a `role: 'notice'` row of kind `failed`.
 * @param {string} [props.retryLabel] — label of the notice's Retry action.
 * @param {((notice: object) => void)|null} [props.onRetry] — Retry handler for notice rows (re-send the last prompt); omit to hide the button.
 * @param {object} props.chatInputProps — forwarded to {@link ChatInput}; optional **`composerMountKey`** (number) forces a remount when incremented by the host after send.
 */
const ChatPanelConversationView = ({
  messages = [],
  isLoadingMessages = false,
  agentIsTyping = false,
  isStreaming = false,
  streamingStatusLabel = null,
  messageSearch = null,
  conversationScrollKey = '',
  onOpenMessageAsDocument,
  documentCreatedMessageIds = [],
  documentCardMetaByMessageId = {},
  alignMessagesStart = false,
  aboveComposerSlot = null,
  activityRunningLabel = 'Working…',
  activityToggleLabel = 'Toggle activity steps',
  formatActivitySummary = null,
  noReplyLabel = 'No reply was generated.',
  failedLabel = 'Something went wrong while generating the reply.',
  retryLabel = 'Retry',
  onRetry = null,
  chatInputProps,
}) => {
  const { composerMountKey = 0, ...chatInputRest } = chatInputProps ?? {};
  const listRef = useRef(null);
  const isUserScrolledUpRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const [isTypewriterTyping, setIsTypewriterTyping] = useState(false);

  const handleTypewriterStateChange = useCallback((typing) => {
    setIsTypewriterTyping(Boolean(typing));
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom <= 20;
    if (scrollTop < lastScrollTopRef.current) isUserScrolledUpRef.current = true;
    if (isAtBottom) isUserScrolledUpRef.current = false;
    lastScrollTopRef.current = scrollTop;
  }, []);

  const isVisuallyTyping = agentIsTyping || isTypewriterTyping;
  const autoScrollActive = isVisuallyTyping || isStreaming;

  const streamingStatusVariant =
    chatInputProps?.processingGradientVariant === 'greenBlue' ? 'greenBlue' : 'bluePurple';
  const prevAutoScrollRef = useRef(false);

  useEffect(() => {
    isUserScrolledUpRef.current = false;
    lastScrollTopRef.current = 0;
    const t = setTimeout(() => scrollToBottom(), 0);
    return () => clearTimeout(t);
  }, [conversationScrollKey, scrollToBottom]);

  useEffect(() => {
    if (isUserScrolledUpRef.current) return;
    requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
  }, [messages?.length, autoScrollActive, scrollToBottom]);

  // Reveal the inline HITL gate card when it appears at the end of the thread.
  // Depend on presence, not the JSX node — the element gets a new identity on
  // every parent render, which would re-fire this effect throughout streaming.
  const hasAboveComposerSlot = Boolean(aboveComposerSlot);
  useEffect(() => {
    if (!hasAboveComposerSlot || isUserScrolledUpRef.current) return;
    requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));
  }, [hasAboveComposerSlot, scrollToBottom]);

  useEffect(() => {
    const was = prevAutoScrollRef.current;
    prevAutoScrollRef.current = autoScrollActive;
    if (was && !autoScrollActive && !isUserScrolledUpRef.current) {
      requestAnimationFrame(() => scrollToBottom());
    }
  }, [autoScrollActive, scrollToBottom]);

  useEffect(() => {
    if (!autoScrollActive) return undefined;
    const id = setInterval(() => {
      if (isUserScrolledUpRef.current) return;
      scrollToBottom();
    }, 80);
    return () => clearInterval(id);
  }, [autoScrollActive, scrollToBottom]);

  const handleCopy = useCallback((text) => {
    const t = typeof text === 'string' ? text : '';
    if (!t) return;
    void navigator.clipboard?.writeText(t).catch(() => {});
  }, []);

  const searchTrimmed = messageSearch?.searchValue?.trim() ?? '';
  const hasSearch = Boolean(messageSearch && searchTrimmed);

  const lastMsg = messages.length ? messages[messages.length - 1] : null;
  const lastIsUser = lastMsg?.role === 'user';
  const showTrailingAssistantSkeleton =
    (agentIsTyping || isStreaming) && (!lastMsg || lastIsUser);

  const bubbleMaxClass = alignMessagesStart
    ? 'max-w-[min(100%,48rem)]'
    : 'max-w-[min(100%,36rem)]';
  const rowJustifyClass = (isUser) =>
    alignMessagesStart ? 'justify-start' : isUser ? 'justify-end' : 'justify-start';
  const streamingBarWrapClass = alignMessagesStart
    ? 'w-full max-w-[min(100%,48rem)] py-2'
    : 'mx-auto w-full max-w-[min(100%,36rem)] py-2';

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ChatPanelSkeleton show={isLoadingMessages} alignMessagesStart={alignMessagesStart} />

      <div
        className={`flex min-h-0 flex-1 flex-col ${isLoadingMessages ? 'pointer-events-none opacity-40' : ''}`}
      >
        <div
          ref={listRef}
          className="scrollbar-aimsos-auto min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3 sm:px-4 hover:[&::-webkit-scrollbar-track]:bg-[#1f2937] hover:[&::-webkit-scrollbar-thumb]:bg-[#6b7280] dark:hover:[&::-webkit-scrollbar-track]:bg-[#111827] dark:hover:[&::-webkit-scrollbar-thumb]:bg-[#9ca3af]"
          data-messages-list
          onScroll={handleMessagesScroll}
        >
          {messages.map((m, index) => {
            const key = m.id || m.messageUuid || `m-${index}`;
            const messageId = m.id || m.messageUuid;

            // Run-activity group: the collapsible per-run record of what a
            // workflow did (progress steps + intermediate milestone messages).
            // Renders inline at the run's position in the thread and stays
            // there — only the run's final message follows it as a bubble.
            if (m.role === 'activity_group') {
              const groupSteps = Array.isArray(m.steps) ? m.steps : EMPTY_STEPS;
              return (
                <div
                  key={key}
                  ref={messageSearch?.setMessageRef ? messageSearch.setMessageRef(index) : undefined}
                  className={`flex w-full min-w-0 ${rowJustifyClass(false)}`}
                >
                  <RunActivityGroup
                    steps={groupSteps}
                    isRunning={m.activityStatus !== 'done'}
                    runningFallback={activityRunningLabel}
                    summaryText={
                      formatActivitySummary ? formatActivitySummary(groupSteps.length) : ''
                    }
                    toggleLabel={activityToggleLabel}
                    maxWidthClass={bubbleMaxClass}
                  />
                </div>
              );
            }

            // Terminal-state notice: the turn ended with nothing to show (the
            // run failed, or completed without text). A compact system row
            // with a Retry action — never an assistant bubble.
            if (m.role === 'notice') {
              return (
                <div
                  key={key}
                  ref={messageSearch?.setMessageRef ? messageSearch.setMessageRef(index) : undefined}
                  className={`flex w-full min-w-0 ${rowJustifyClass(false)}`}
                >
                  <ChatNoticeRow
                    notice={m}
                    noReplyLabel={noReplyLabel}
                    failedLabel={failedLabel}
                    retryLabel={retryLabel}
                    onRetry={onRetry}
                    retryDisabled={Boolean(chatInputProps?.isProcessing || chatInputProps?.disabled)}
                    maxWidthClass={bubbleMaxClass}
                  />
                </div>
              );
            }

            const isUser = m.role === 'user';
            const isLastAgentMessage = !isUser && index === messages.length - 1;
            const isStreamingMessage =
              !isUser && (agentIsTyping || isStreaming) && isLastAgentMessage;
            const content = normalizeChatMessageContent(m.content) || '';
            const isEmptyAssistantStreaming =
              !isUser &&
              isLastAgentMessage &&
              (agentIsTyping || isStreaming) &&
              !content.trim();

            if (isEmptyAssistantStreaming) {
              return (
                <div
                  key={key}
                  ref={messageSearch?.setMessageRef ? messageSearch.setMessageRef(index) : undefined}
                  className="flex w-full min-w-0 justify-start"
                >
                  <ChatPanelAssistantMessageSkeleton variant="pending" />
                </div>
              );
            }

            const matchesInMessage =
              hasSearch && messageSearch.matches?.length
                ? messageSearch.matches.filter((x) => x.messageIndex === index)
                : [];
            const showHighlights = matchesInMessage.length > 0;
            const html =
              isUser &&
              showHighlights &&
              buildHighlightedHtml(content, matchesInMessage, messageSearch.currentMatchIndex);
            const agentHighlightMarkdown =
              !isUser && showHighlights
                ? buildMarkdownWithHighlights(
                    content,
                    matchesInMessage,
                    messageSearch.currentMatchIndex,
                  )
                : null;
            const cardTitle = documentCardMetaByMessageId[messageId]?.title || 'Document from message';
            const cardDescription = toPlainTextDescription(
              documentCardMetaByMessageId[messageId]?.description || '',
            );

            const attachmentList =
              Array.isArray(m.attachments) && m.attachments.length > 0 ? (
                <ul className="mt-2 space-y-1 border-t border-edge/60 pt-2 text-xs text-content-muted">
                  {m.attachments.map((att, i) => (
                    <li key={att.fileUuid || att.id || i}>
                      {(att.name || 'Attachment') + (att.fileType ? ` · ${att.fileType}` : '')}
                    </li>
                  ))}
                </ul>
              ) : null;

            return (
              <div key={key} ref={messageSearch?.setMessageRef ? messageSearch.setMessageRef(index) : undefined}>
                <div className={`flex w-full min-w-0 ${rowJustifyClass(isUser)}`}>
                  <div
                    className={`group relative ${bubbleMaxClass} min-w-0 rounded-xl px-3 py-2 text-sm ${
                      isUser
                        ? 'bg-brand-50 text-content dark:bg-brand-900/30'
                        : 'border border-edge bg-surface-muted text-content'
                    }`}
                  >
                    {isUser ? (
                      <div className="flex items-start justify-between gap-2">
                        {showHighlights && html ? (
                          <div
                            className="min-w-0 flex-1 whitespace-pre-wrap break-words [&_.highlight-current-js]:rounded-sm"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        ) : (
                          <p className="min-w-0 flex-1 whitespace-pre-wrap break-words">{content}</p>
                        )}
                        <div className="flex shrink-0 items-center gap-1">
                          <Tooltip content="Copy" position="top" align="end" arrow={false}>
                            <button
                              type="button"
                              className="rounded p-1 text-content-muted opacity-0 transition-opacity hover:bg-surface-hover hover:text-content group-hover:opacity-100"
                              aria-label="Copy message"
                              onClick={() => handleCopy(content)}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="min-w-0">
                          <ChatMessageMarkdown
                            content={m.content}
                            highlightMarkdown={agentHighlightMarkdown}
                            isStreaming={isStreamingMessage}
                            onTypingStateChange={
                              isLastAgentMessage ? handleTypewriterStateChange : undefined
                            }
                          />
                        </div>
                        {attachmentList}
                        <div className="mt-2 flex items-center justify-end gap-1 border-t border-edge/60 pt-2">
                          <Tooltip content="Copy" position="bottom" align="end" arrow={false}>
                            <button
                              type="button"
                              className="rounded p-1 text-content-muted opacity-0 transition-opacity hover:bg-surface-hover hover:text-content group-hover:opacity-100"
                              aria-label="Copy message"
                              onClick={() => handleCopy(content)}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          {onOpenMessageAsDocument ? (
                            <Tooltip content="Open as document" position="bottom" align="end" arrow={false}>
                              <button
                                type="button"
                                className="rounded p-1 text-content-muted opacity-0 transition-opacity hover:bg-surface-hover hover:text-content group-hover:opacity-100"
                                aria-label="Open as document"
                                onClick={() => onOpenMessageAsDocument(m)}
                              >
                                <FileText className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          ) : null}
                        </div>
                      </>
                    )}
                    {isUser ? attachmentList : null}
                  </div>
                </div>
                {!isUser && documentCreatedMessageIds.includes(messageId) && onOpenMessageAsDocument && (
                  <div className="mt-2 flex justify-start">
                    <div
                      className={`w-full min-w-0 ${bubbleMaxClass} rounded-xl border border-edge bg-surface px-3 py-3`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-content">
                            {cardTitle}
                          </p>
                          {cardDescription ? (
                            <p className="mt-1 truncate text-xs text-content-secondary">
                              {cardDescription}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          size="sm"
                          variant="primary"
                          className="shrink-0 whitespace-nowrap"
                          onClick={() => onOpenMessageAsDocument(m)}
                        >
                          Open document
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {showTrailingAssistantSkeleton ? (
            <div className="flex w-full min-w-0 justify-start">
              <ChatPanelAssistantMessageSkeleton variant="pending" />
            </div>
          ) : null}
          {(agentIsTyping || isStreaming) && (
            <div className={streamingBarWrapClass}>
              {streamingStatusLabel ? (
                <p className="mb-1 text-xs text-content-muted" aria-live="polite">
                  {streamingStatusLabel}
                </p>
              ) : null}
              <StreamingStatusBar
                variant={streamingStatusVariant}
                minWidthClassName="min-w-0"
                className="w-full"
                ariaLabel={streamingStatusLabel || 'Streaming assistant response'}
              />
            </div>
          )}
          {/* HITL gate card renders inline as the last item in the thread —
              right under the last message, top-aligned like any chat, so it
              scrolls with the conversation instead of leaving a gap above the
              composer. */}
          {aboveComposerSlot ? (
            <div className="min-w-0 pt-1">{aboveComposerSlot}</div>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-edge bg-surface px-3 pb-3 pt-2 sm:px-4 dark:bg-surface">
          <ChatInput
            key={composerMountKey}
            className="w-full min-w-0"
            growDirection="up"
            {...chatInputRest}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatPanelConversationView;
